import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft,
  AudioLines,
  CheckCircle2,
  Flag,
  Loader2,
  Music,
  Play,
  Search,
  Trash2,
  Undo2,
  XCircle,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { SiteLogo } from "@/components/SiteLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SITE_NAME } from "@shared/site";
import type { Song, SongStatus } from "@shared/songTypes";

const STATIC_STATIONS = [
  "wesellpickleball",
  "rockthecourt",
  "dinkandtwang",
  "dinkdontstop",
  "popanddink",
  "smashanddrop",
  "graceanddink",
  "winddown",
];

const STATION_LABEL: Record<string, string> = {
  wesellpickleball: "The Pickle Jar",
  rockthecourt: "Rock the Court",
  dinkandtwang: "Dink & Twang",
  dinkdontstop: "Dink Don't Stop",
  popanddink: "Pop & Dink",
  smashanddrop: "Smash & Drop",
  graceanddink: "Grace & Dink",
  winddown: "Wind Down",
};

function mediaUrl(song: Song): string {
  return `/api/songs/media/${song.relPath}`;
}

function StatusBadge({ status }: { status: SongStatus }) {
  if (status === "removed")
    return (
      <Badge variant="destructive" className="gap-1">
        <Trash2 className="w-3 h-3" /> Remove
      </Badge>
    );
  if (status === "flagged")
    return (
      <Badge variant="outline" className="gap-1 border-amber-500 text-amber-500">
        <Flag className="w-3 h-3" /> Flagged
      </Badge>
    );
  return (
    <Badge variant="secondary" className="gap-1">
      <CheckCircle2 className="w-3 h-3" /> Active
    </Badge>
  );
}

function SongRow({ song, onRefresh }: { song: Song; onRefresh: () => void }) {
  const [showLyrics, setShowLyrics] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const transcribe = trpc.songs.transcribe.useMutation({
    onSuccess: onRefresh,
    onSettled: () => setTranscribing(false),
  });

  const setStatus = trpc.songs.setStatus.useMutation({ onSuccess: onRefresh });

  const phraseHit = song.phraseHit;
  const hasLyrics = !!song.lyrics;

  return (
    <div
      className={`rounded-lg border p-3 space-y-2 ${
        phraseHit ? "border-red-500/70 bg-red-500/5" : "border-border bg-card"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {phraseHit && (
              <Badge variant="destructive" className="gap-1">
                <AudioLines className="w-3 h-3" /> "we sell pickleball"
              </Badge>
            )}
            <StatusBadge status={song.status} />
          </div>
          <p className="font-semibold mt-1.5 leading-tight">{song.title}</p>
          <p className="text-xs text-muted-foreground truncate font-mono mt-0.5">
            {song.filename}
          </p>
        </div>
      </div>

      <audio
        key={song.id}
        controls
        preload="metadata"
        className="w-full h-9"
        src={mediaUrl(song)}
      />

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {!hasLyrics && (
          <Button
            size="sm"
            variant="outline"
            disabled={transcribing || transcribe.isPending}
            onClick={() => {
              setTranscribing(true);
              transcribe.mutate({ id: song.id });
            }}
            className="gap-1.5"
          >
            {transcribing || transcribe.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Play className="w-3 h-3" />
            )}
            Transcribe lyrics
          </Button>
        )}
        {hasLyrics && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowLyrics(v => !v)}
            className="gap-1.5"
          >
            {showLyrics ? "Hide" : "Show"} lyrics
          </Button>
        )}
        {song.status !== "removed" && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setStatus.mutate({ id: song.id, status: "removed" })}
            className="gap-1.5"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </Button>
        )}
        {song.status === "removed" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setStatus.mutate({ id: song.id, status: "active" })}
            className="gap-1.5"
          >
            <Undo2 className="w-3 h-3" /> Undo
          </Button>
        )}
        {song.status !== "flagged" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setStatus.mutate({ id: song.id, status: "flagged" })}
            className="gap-1.5"
          >
            <Flag className="w-3 h-3" /> Flag
          </Button>
        )}
      </div>

      {showLyrics && song.lyrics && (
        <pre className="text-xs whitespace-pre-wrap font-sans text-muted-foreground border border-border rounded-md p-3 max-h-64 overflow-auto">
          {song.lyrics}
        </pre>
      )}
    </div>
  );
}

/** Song Audit — browse every station's tracks, listen, read lyrics, flag/remove. */
export default function SongAudit() {
  const { data: me, isLoading: meLoading } = trpc.session.me.useQuery();
  const [station, setStation] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [phraseOnly, setPhraseOnly] = useState(false);

  const { data, refetch, isFetching } = trpc.songs.list.useQuery(
    {
      station: station === "all" ? undefined : station,
      query: query.trim() || undefined,
      phraseHitsOnly: phraseOnly || undefined,
    },
    { enabled: !!me && me.role === "admin" }
  );

  const { data: flaggedSummary, refetch: refetchFlags } =
    trpc.songs.flaggedSummary.useQuery(undefined, {
      enabled: !!me && me.role === "admin",
    });

  useEffect(() => {
    document.title = `Song Audit — ${SITE_NAME}`;
  }, []);

  const refresh = () => {
    refetch();
    refetchFlags();
  };

  const songs = data?.songs ?? [];

  if (meLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me || me.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <XCircle className="w-12 h-12 text-destructive" />
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground">Admin access required.</p>
        <Link href="/">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Radio
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border py-4 px-4 flex items-center gap-4 flex-wrap">
        <Link href="/admin">
          <Button variant="ghost" size="icon" aria-label="Back to admin">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <SiteLogo className="h-8" />
        <div className="min-w-0">
          <h1 className="font-bold leading-tight">{SITE_NAME} Song Audit</h1>
          <p className="text-xs text-muted-foreground">
            Listen, read lyrics, flag for removal
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {flaggedSummary && flaggedSummary.hitCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AudioLines className="w-3 h-3" />
              {flaggedSummary.hitCount}{" "}
              {flaggedSummary.hitCount === 1 ? "hit" : "hits"} — "
              {flaggedSummary.phrase}"
            </Badge>
          )}
          {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={station}
            onChange={e => setStation(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
            aria-label="Filter by station"
          >
            <option value="all">All stations</option>
            {STATIC_STATIONS.map(s => (
              <option key={s} value={s}>
                {STATION_LABEL[s]}
              </option>
            ))}
          </select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search title, file, or lyrics…"
              className="pl-8"
            />
          </div>

          <Button
            variant={phraseOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setPhraseOnly(v => !v)}
            className="gap-1.5"
          >
            <AudioLines className="w-3 h-3" /> "we sell pickleball" only
          </Button>
        </div>

        <p className="text-sm text-muted-foreground">
          {songs.length} of {data?.total ?? 0} songs shown
        </p>

        {songs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No songs match.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {songs.map(s => (
              <SongRow key={s.id} song={s} onRefresh={refresh} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}